const TIME_TOKEN_PATTERN = /\$time\|([^$|]+)\|([^$]+)\$/g
const TIME_SELECTOR = '[data-lay-time]'
const SKIP_TAGS = ['SCRIPT', 'STYLE', 'TEXTAREA', 'INPUT', 'SELECT', 'OPTION']

let timerId = null
let timerInterval = null

const normalizeRoot = (root) => {
	if (!root) {
		return document.body
	}
	if (root.jquery) {
		return root[0]
	}
	return root
}

const pad = (value, length = 2) => String(value).padStart(length, '0')

const parseGmtOffset = (timezone) => {
	const match = String(timezone).trim().match(/^(?:GMT|UTC)([+-])(\d{1,2})(?::?(\d{2}))?$/i)
	if (!match) {
		return null
	}

	const hours = parseInt(match[2], 10)
	const minutes = match[3] ? parseInt(match[3], 10) : 0
	if (hours > 23 || minutes > 59) {
		return null
	}

	const multiplier = match[1] === '-' ? -1 : 1
	return multiplier * (hours * 60 + minutes)
}

const getPartsForOffset = (date, offsetMinutes) => {
	const shifted = new Date(date.getTime() + offsetMinutes * 60000)
	return {
		hour24: shifted.getUTCHours(),
		minute: shifted.getUTCMinutes(),
		second: shifted.getUTCSeconds(),
		millisecond: shifted.getUTCMilliseconds(),
	}
}

const getPartsForTimezone = (date, timezone) => {
	const offsetMinutes = parseGmtOffset(timezone)
	if (offsetMinutes !== null) {
		return getPartsForOffset(date, offsetMinutes)
	}

	try {
		const formatter = new Intl.DateTimeFormat('en-US', {
			timeZone: timezone,
			hourCycle: 'h23',
			hour: '2-digit',
			minute: '2-digit',
			second: '2-digit',
		})
		const parts = formatter.formatToParts(date).reduce((acc, part) => {
			acc[part.type] = part.value
			return acc
		}, {})

		return {
			hour24: parseInt(parts.hour, 10),
			minute: parseInt(parts.minute, 10),
			second: parseInt(parts.second, 10),
			millisecond: date.getMilliseconds(),
		}
	} catch (e) {
		return {
			hour24: date.getHours(),
			minute: date.getMinutes(),
			second: date.getSeconds(),
			millisecond: date.getMilliseconds(),
		}
	}
}

const formatTime = (timezone, format, date = new Date()) => {
	const parts = getPartsForTimezone(date, timezone)
	const hour12Value = parts.hour24 % 12 || 12
	const values = {
		SSS: pad(parts.millisecond, 3),
		SS: pad(parts.millisecond, 3).slice(0, 2),
		HH: pad(parts.hour24),
		hh: pad(hour12Value),
		mm: pad(parts.minute),
		ss: pad(parts.second),
		a: parts.hour24 >= 12 ? 'pm' : 'am',
	}
	let result = ''

	for (let i = 0; i < format.length; ) {
		const three = format.slice(i, i + 3)
		if (three === 'SSS') {
			result += values.SSS
			i += 3
			continue
		}

		const two = format.slice(i, i + 2)
		if (Object.prototype.hasOwnProperty.call(values, two)) {
			result += values[two]
			i += 2
			continue
		}

		const one = format.slice(i, i + 1)
		if (Object.prototype.hasOwnProperty.call(values, one)) {
			result += values[one]
			i += 1
			continue
		}

		result += one
		i += 1
	}

	return result
}

const shouldSkipTextNode = (node) => {
	const parent = node && node.parentElement
	if (!parent || SKIP_TAGS.includes(parent.tagName)) {
		return true
	}
	return parent.closest(TIME_SELECTOR) !== null
}

const createTimeElement = (timezone, format) => {
	const el = document.createElement('span')
	el.className = 'lay-current-time'
	el.setAttribute('data-lay-time', '1')
	el.setAttribute('data-lay-time-timezone', timezone)
	el.setAttribute('data-lay-time-format', format)
	el.textContent = formatTime(timezone, format)
	return el
}

const replaceTimeTokensInTextNode = (node) => {
	if (shouldSkipTextNode(node) || !node.nodeValue.includes('$time|')) {
		return false
	}

	TIME_TOKEN_PATTERN.lastIndex = 0
	const matches = Array.from(node.nodeValue.matchAll(TIME_TOKEN_PATTERN))
	if (!matches.length) {
		return false
	}

	const fragment = document.createDocumentFragment()
	let lastIndex = 0
	matches.forEach((match) => {
		if (match.index > lastIndex) {
			fragment.appendChild(document.createTextNode(node.nodeValue.slice(lastIndex, match.index)))
		}
		fragment.appendChild(createTimeElement(match[1].trim(), match[2].trim()))
		lastIndex = match.index + match[0].length
	})
	if (lastIndex < node.nodeValue.length) {
		fragment.appendChild(document.createTextNode(node.nodeValue.slice(lastIndex)))
	}

	node.parentNode.replaceChild(fragment, node)
	return true
}

const replaceRawTokens = (root) => {
	if (!root) {
		return
	}
	if (root.nodeType === Node.TEXT_NODE) {
		replaceTimeTokensInTextNode(root)
		return
	}
	if (!root.querySelectorAll) {
		return
	}

	const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
		acceptNode: (node) => (shouldSkipTextNode(node) || !node.nodeValue.includes('$time|') ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT),
	})
	const nodes = []
	while (walker.nextNode()) {
		nodes.push(walker.currentNode)
	}
	nodes.forEach(replaceTimeTokensInTextNode)
}

const updateTimeElements = (root) => {
	const target = normalizeRoot(root)
	if (!target) {
		return
	}

	const date = new Date()
	const updateElement = (el) => {
		const timezone = el.getAttribute('data-lay-time-timezone') || ''
		const format = el.getAttribute('data-lay-time-format') || ''
		if (timezone && format) {
			el.textContent = formatTime(timezone, format, date)
		}
	}

	if (target.matches && target.matches(TIME_SELECTOR)) {
		updateElement(target)
	}
	if (target.querySelectorAll) {
		target.querySelectorAll(TIME_SELECTOR).forEach(updateElement)
	}
}

const getRequiredInterval = () => {
	const times = document.querySelectorAll(TIME_SELECTOR)
	if (!times.length) {
		return null
	}

	for (let i = 0; i < times.length; i += 1) {
		if ((times[i].getAttribute('data-lay-time-format') || '').includes('SS')) {
			return 10
		}
	}

	return 1000
}

const refreshTimer = () => {
	const requiredInterval = getRequiredInterval()
	if (requiredInterval === timerInterval) {
		return
	}

	if (timerId !== null) {
		window.clearInterval(timerId)
		timerId = null
	}

	timerInterval = requiredInterval
	if (requiredInterval !== null) {
		timerId = window.setInterval(() => {
			updateTimeElements(document.body)
		}, requiredInterval)
	}
}

const updateTimePlaceholders = (root) => {
	const target = normalizeRoot(root)
	if (!target) {
		return
	}

	replaceRawTokens(target)
	updateTimeElements(target)
	refreshTimer()
}

const time_controller = {
	i: function () {
		updateTimePlaceholders(document.body)
		window.laytheme.on('newpageshown', function () {
			updateTimePlaceholders(document.body)
		})
		window.laytheme.on('content_ready', function (typeOfContentThatIsReady, $target) {
			updateTimePlaceholders($target)
		})
	},
	update: updateTimePlaceholders,
	formatTime: formatTime,
}

export default time_controller
